import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { EngagementSurveyResponsesPageDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { CycleSelector } from 'modules/engagement_surveys/components/CycleSelector'
import { ParticipationCard } from 'modules/engagement_surveys/components/ParticipationCard'
import { useSurveyActions } from 'modules/engagement_surveys/hooks/useSurveyActions'
import {
  type FormFiltersStorage,
  FormResponses,
  TableColumnType,
  useFormCycleSelection,
} from 'modules/forms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export default function SurveyResponsesPage() {
  const { surveyId } = useStrictParams<'surveyId'>()
  const { data, loading, error } = useQuery(EngagementSurveyResponsesPageDocument, {
    variables: { surveyId: Number(surveyId) },
    nextFetchPolicy: 'cache-first',
  })

  const survey = data?.surveys.survey
  const form = survey?.form

  const formId = form?.id.toString() || ''
  const [savedStorage] = useLocalStorage<FormFiltersStorage | undefined>(
    LOCAL_STORAGE_KEYS.formResponseFilters(formId)
  )

  const { cycles, selectedCycleId, handleCycleChange, hasCycles } = useFormCycleSelection(
    survey,
    savedStorage ?? undefined
  )

  const materializedFormIds = useMemo(
    () => (selectedCycleId ? [selectedCycleId] : []),
    [selectedCycleId]
  )

  const { handleActionClick } = useSurveyActions({ formId })

  return (
    <ResponsivePage.Body loading={loading} error={error} variant="fullscreen" onActionClick={handleActionClick}>
      {form ? (
        <Box paddingTop="s16">
          <FormResponses
            formId={formId}
            columns={[TableColumnType.Participant, TableColumnType.Status]}
            filters={[
              'teamIds',
              'locationIds',
              'legalEntityIds',
              'managerIds',
              'jobRoles',
              'jobTitles',
            ]}
            persistFilters
            resourceType="Surveys::Survey"
            materializedFormResourceType="Surveys::Survey"
            materializedFormIds={selectedCycleId ? [selectedCycleId] : null}
            cycleSelector={
              hasCycles ? (
                <CycleSelector
                  cycles={cycles}
                  selectedCycleId={selectedCycleId}
                  onCycleChange={handleCycleChange}
                />
              ) : undefined
            }
            headerContent={(filters) => (
              <ParticipationCard
                formId={formId}
                materializedFormIds={materializedFormIds}
                filters={filters}
                formEndsAt={form.endsAt}
                daysToAnswer={form.configuration?.daysToAnswer}
                status={form.status}
                shouldSkip={materializedFormIds.length === 0}
              />
            )}
          />
        </Box>
      ) : null}
    </ResponsivePage.Body>
  )
}
